/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, useRef, Fragment } from "react";
import { string, number, func, bool, any } from "prop-types";
import { connect } from "react-redux";
import * as style from "./styles.module.scss";
import ReadingStatus from "./reading_status";
import Checkbox from "../../common/Checkbox/index";
import {
  getRegattaSignatureDocs,
  getCompleatFoodSignatureDocs,
  getPusterlaPDF,
} from "../../api/profileApi";
import Spinner from "../../common/Spinner";
import LoadingSpinner from "../../icons/LoadingSpinner";

const filenameFromUrl = (url) => url.split("/").slice(-1)[0];

const isiOSDevice = () => {
  return (
    [
      "iPad Simulator",
      "iPhone Simulator",
      "iPod Simulator",
      "iPad",
      "iPhone",
      "iPod",
    ].includes(navigator.platform) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  );
};

const isDownloadAttrSupported = () => "download" in document.createElement("a");

const ReadingListDownload = ({
  appData,
  title,
  description,
  link,
  onFly,
  status = ReadingStatus.Todo,
  currentlyReading,
  setCurrentlyReading,
  markDocumentRead,
  getCompletedDocument,
  downloadedPdfs,
  isCustom = false,
  customAgreements,
  agreementText,
  isLoading = false,
  isGB,
  isKingspan,
  isPecan,
  isPusterla,
  noCheckBoxConsent,
  importantMsg,
  msg,
}) => {
  const filename = filenameFromUrl(link);
  const [downloadingFile, setDownloadingFile] = useState();
  const [multipleAgreementsRead, setMultipleAgreementsRead] = useState(0);
  const [secondTickbox, setSecondTickbox] = useState(false);
  const [firstTickbox, setFirstTickbox] = useState(false);
  const downloadRef = useRef();
  const [loadingPDF, setLoadingPDF] = useState(false);

  const handleDownload = (e) => {
    window.open(link, "_blank");
    if (isiOSDevice() || !isDownloadAttrSupported()) {
      e.preventDefault();
    }
    if (status === ReadingStatus.Todo) {
      setCurrentlyReading(filename);
    }
    return true;
  };

  const displayCustomTickboxText = () => {
    if (isPecan) {
      if (filename === "iduction-booklet.pdf") {
        return (
          <Fragment>
            I confirm that I have been trained and fully understand the
            requirements of the below named documents;
            <br />
            · Agency Hygiene Regulations
            <br />
            · Agency Employees Acceptance of Food Hygiene Rules
            <br />
            · Agency Fire Regulations
            <br />
            · Food Safety, Legality and Quality Assurance Policy – TEC001
            <br />
            · Personal Health, Hygiene & General Standards Policy – TR002
            <br />
            · Allergen Awareness Training – TR004
            <br />
            · Pest Awareness
            <br />
            · Personal Protection Clothing (PPC) Equipment (PPE) Policy – Agency
            – TR050
            <br />
            · Manual Handling Booklet – MHB001
            <br />
            · Halal Awareness – HALAL001
            <br />
            · Ethical Trading Policy (ETI) Base Code – ID0006
            <br />
            · Pre-Employment Heath Questionnaire – TR009
            <br />
            · Employee & Next of Kin Details
            <br />· Assessment Questionnaire
          </Fragment>
        );
      }
      if (filename === "hygiene-regulations.pdf") {
        return "I have read and understood the Hygiene Regulations.";
      }
      if (filename === "food-hygiene-rules-copy.pdf") {
        return "I understand and accept the Hygiene Rules.";
      }
    }
    if (isKingspan) {
      if (filename === "gn-sop-009-isolation-procedure-agency.pdf") {
        return "I confirm I have read and understood the Isolation Procedure document and agree to what has been stated.";
      }
      if (filename === "holywell-agency-induction-2022.pdf") {
        return "I confirm I have read and understood the Induction and agree to what has been stated.";
      }
      if (filename === "manual_handling_handbook.pdf") {
        return "I confirm I have read and understood the Handbook and agree to what has been stated.";
      }
      if (filename === "drug-and-alcohol-policy-agency-workers-v2.pdf")
        return "I confirm I have read and understood the Drug & Alcohol Policy and agree to what has been stated.";
    }
    return "I confirm I have read and understood the document and agree to what has been stated.";
  };

  const showOnFlyDocument = (response) => {
    setTimeout(null, 1000); // so download is not instantaneous
    const onFlyLink = document.createElement("a");
    const blob = new Blob([response.data], {
      type: "application/pdf",
    });
    onFlyLink.href = window.URL.createObjectURL(blob);
    onFlyLink.download = `${filename}`;
    onFlyLink.click();
    setLoadingPDF(false);
  };

  const handleCustomDownload = () => {
    if (onFly) {
      if (
        link ===
        "https://www.hrgo.co.uk/media/8027/working-time-opt-out-form.pdf"
      ) {
        return getCompleatFoodSignatureDocs(
          link,
          `${appData.firstName} ${appData.lastName}`
        ).then((response) => showOnFlyDocument(response));
      }
      if (
        link ===
        "https://www.hrgo.co.uk/media/8114/sow-no-29-glue-lines-march-22.pdf"
      ) {
        return getPusterlaPDF("YPUS").then((response) =>
          showOnFlyDocument(response)
        );
      }
      return getRegattaSignatureDocs(
        link,
        `${appData.firstName} ${appData.lastName}`
      ).then((response) => showOnFlyDocument(response));
    }
    if (isPusterla) {
      return handleDownload();
    }
    // download all other files regular way, except of drug and policy form
    if (
      isKingspan &&
      filename !== "drug-and-alcohol-policy-agency-workers-v2.pdf"
    ) {
      return handleDownload();
    }
    if (!downloadingFile) getCompletedDocument(title);

    return setDownloadingFile(true);
  };

  useEffect(() => {
    if (downloadingFile && isCustom) {
      const pdfElem =
        downloadedPdfs && downloadedPdfs.find((e) => e.name === title);
      // eslint-disable-next-line
      const blob = new Blob([pdfElem.pdf], {
        type: "application/pdf",
      });
      downloadRef.current.href = window.URL.createObjectURL(blob);
      downloadRef.current.download = `${title}.pdf`;
      downloadRef.current.click();

      setDownloadingFile(false);
    }
  }, [downloadedPdfs]);

  const cancelRead = () => {
    setCurrentlyReading(null);
    setMultipleAgreementsRead(0);
  };

  const handleMarkRead = () => {
    setCurrentlyReading(null);
    if (isKingspan) {
      markDocumentRead(filename);
      return;
    }
    markDocumentRead(filename);
  };

  const markRead = () => {
    if (customAgreements && isCustom) {
      setCurrentlyReading(null);
      handleMarkRead();
    } else {
      handleMarkRead();
    }
  };

  const handleDownloadNoConsent = (e) => {
    markDocumentRead(filename);
    window.open(link, "_blank");
    if (isiOSDevice() || !isDownloadAttrSupported()) {
      e.preventDefault();
    }
  };

  const markGB = (tickbox) => {
    if (tickbox === "first") {
      if (secondTickbox) {
        return markRead();
      }
      setFirstTickbox(!firstTickbox);
    }
    if (tickbox === "second") {
      if (firstTickbox) {
        return markRead();
      }
      setSecondTickbox(!secondTickbox);
    }
    return null;
  };

  if (currentlyReading === filename) {
    return (
      <div
        className={`${style.rldownload__wrapper} ${style.rldownload__wrapper__reading}`}
      >
        {customAgreements && !isCustom ? (
          <div
            className={`d-flex flex-column ${style.rldownload__multipleAgreements}`}
          >
            <div className={style.rldownload__multipleAgreements_inner}>
              <h3>{`${title} Agreement${
                customAgreements.length > 1 ? "s" : ""
              }`}</h3>
              {customAgreements.map((customAgreement, i) => (
                <span className={style.rldownload__agreement_custom} key={i}>
                  {customAgreement.title && (
                    <b className={style.rldownload__agreement_customTitle}>
                      {customAgreement.title}
                    </b>
                  )}
                  <Checkbox
                    label={customAgreement.text || ""}
                    value={filename}
                    onClick={markRead}
                  />
                </span>
              ))}
              <div className="d-flex w-100 justify-content-end">
                <button
                  className="btn btn-outline-info text-dark"
                  onClick={cancelRead}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className={style.rldownload__agreement}>
            {isGB ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: "1rem",
                }}
              >
                <span className={style.rldownload__agreement}>
                  <input
                    type="checkbox"
                    name="document_read"
                    value={filename}
                    onClick={() => markGB("first")}
                  />
                  {agreementText || `I've read and understood the ${title}.`}
                </span>
                <span className={style.rldownload__agreement}>
                  <input
                    type="checkbox"
                    name="checklist_ticked"
                    value={""}
                    onClick={() => markGB("second")}
                  />
                  I confirm that I have been shown and understood each item on
                  the Induction Checklist.
                </span>
              </div>
            ) : (
              <span className={style.rldownload__agreement}>
                <input
                  type="checkbox"
                  name="document_read"
                  value={filename}
                  onClick={markRead}
                />
                {isKingspan || isPecan
                  ? displayCustomTickboxText()
                  : agreementText || `I've read and understood the ${title}.`}
              </span>
            )}
          </div>
        )}
        <button
          onClick={cancelRead}
          className={`${style.rldownload__button} btn btn-md btn-outline mx-1 mx-sm-0`}
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div key={link} className={`${style.rldownload__wrapper}`}>
      <div className={`${style.rldownload__mobile_row}`}>
        <div
          className={`${style.rldownload__status} ${
            status === ReadingStatus.Todo
              ? style.rldownload__todo
              : style.rldownload__done
          }`}
        >
          {status === ReadingStatus.Todo ? "To Do" : "Done"}
        </div>
        <span
          style={{ fontWeight: isPecan && "700" }}
          className={`${style.rldownload__title}`}
        >
          {title}{" "}
          {msg ? (
            <div className={`${style.rldownload__message}`}>{msg}</div>
          ) : null}
          {description && (
            <span style={{ display: "inline-block", fontSize: "0.8em" }}>
              {description}
            </span>
          )}
        </span>
      </div>

      {importantMsg ? (
        <span className={`${style.rldownload__message_important}`}>
          {importantMsg}
        </span>
      ) : null}

      {/* Just download (no consent checkbox) */}
      {isCustom && noCheckBoxConsent ? (
        <a
          onClick={(e) => {
            handleDownloadNoConsent(e);
          }}
          download={filename}
          style={{ cursor: "pointer" }}
          className={`${style.rldownload__button} ${
            style.rldownload__download
          } btn btn-md btn-outline ${
            (currentlyReading !== null && status === ReadingStatus.Todo) ||
            isLoading
              ? "disabled"
              : ""
          }`}
        >
          Download
        </a>
      ) : isCustom && status !== ReadingStatus.Todo ? (
        <a
          onClick={() => {
            if (onFly) setLoadingPDF(true);
            handleCustomDownload();
          }}
          ref={downloadRef}
          style={{
            cursor: "pointer",
            display: isPecan && status === ReadingStatus.Done && "none",
          }}
          className={`${style.rldownload__button} ${
            style.rldownload__download
          } btn btn-md btn-outline ${
            (currentlyReading !== null && status === ReadingStatus.Todo) ||
            isLoading ||
            loadingPDF
              ? "disabled"
              : ""
          }`}
        >
          {!loadingPDF ? "Download" : <LoadingSpinner />}
        </a>
      ) : (
        <a
          onClick={handleDownload}
          download={filename}
          style={{ cursor: "pointer" }}
          className={`${style.rldownload__button} ${
            style.rldownload__download
          } btn btn-md btn-outline ${
            (currentlyReading !== null && status === ReadingStatus.Todo) ||
            isLoading
              ? "disabled"
              : ""
          }`}
        >
          Download
        </a>
      )}
    </div>
  );
};

ReadingListDownload.propTypes = {
  title: string.isRequired,
  description: string,
  link: string.isRequired,
  status: number,
  currentlyReading: string,
  setCurrentlyReading: func.isRequired,
  markDocumentRead: func,
  getCompletedDocument: func,
  downloadedPdfs: any,
  isCustom: bool,
  customAgreements: any,
  agreementText: any,
  isLoading: bool,
  isGB: bool,
  isKingspan: bool,
  isPecan: bool,
  isPusterla: bool,
  noCheckBoxConsent: bool,
  onFly: bool,
};

const mapStateToProps = (state) => ({
  appData: state.core.app,
});

const withRedux = connect(mapStateToProps, null);

export default withRedux(ReadingListDownload);
